import React, { useState } from "react";

/**
 * Checkers — basic engine: 8x8, alternating squares, simple moves (incl mandatory diagonal jumps).
 * Hot-seat play (red vs black). Supports both local and shared state.
 */
const SIZE = 8;
const initialBoard = () => {
  const b = Array(SIZE).fill(0).map(() => Array(SIZE).fill(null));
  for (let r = 0; r < 3; r++)
    for (let c = 0; c < SIZE; c++)
      if ((r + c) % 2 === 1) b[r][c] = { color: "B", king: false };
  for (let r = 5; r < 8; r++)
    for (let c = 0; c < SIZE; c++)
      if ((r + c) % 2 === 1) b[r][c] = { color: "R", king: false };
  return b;
};

function getMoves(board, r, c) {
  const piece = board[r][c];
  if (!piece) return [];
  const dirs = piece.king
    ? [[-1,-1],[-1,1],[1,-1],[1,1]]
    : piece.color === "R"
      ? [[-1,-1],[-1,1]]
      : [[1,-1],[1,1]];
  const out = [];
  for (const [dr, dc] of dirs) {
    const nr = r + dr, nc = c + dc;
    if (nr<0||nr>=SIZE||nc<0||nc>=SIZE) continue;
    if (!board[nr][nc]) out.push({ r: nr, c: nc, capture: null });
    else if (board[nr][nc].color !== piece.color) {
      const jr = r + 2*dr, jc = c + 2*dc;
      if (jr>=0&&jr<SIZE&&jc>=0&&jc<SIZE&&!board[jr][jc]) {
        out.push({ r: jr, c: jc, capture: { r: nr, c: nc } });
      }
    }
  }
  return out;
}

function countPieces(board) {
  let r = 0, b = 0;
  for (const row of board) for (const p of row) {
    if (p?.color === "R") r++; else if (p?.color === "B") b++;
  }
  return { R: r, B: b };
}

export default function Checkers() {
  const [board, setBoard] = useState(initialBoard);
  const [turn, setTurn] = useState("R");
  const [selected, setSelected] = useState(null);
  const moves = selected ? getMoves(board, selected.r, selected.c) : [];

  const { R, B } = countPieces(board);
  const winner = R === 0 ? "B" : B === 0 ? "R" : null;

  const handleCellClick = (r, c) => {
    if (winner) return;
    const piece = board[r][c];
    if (selected) {
      const move = moves.find(m => m.r === r && m.c === c);
      if (move) {
        const nb = board.map(row => row.map(p => p ? { ...p } : null));
        nb[r][c] = nb[selected.r][selected.c];
        nb[selected.r][selected.c] = null;
        if (move.capture) nb[move.capture.r][move.capture.c] = null;
        // king me
        if (turn === "R" && r === 0) nb[r][c].king = true;
        if (turn === "B" && r === SIZE - 1) nb[r][c].king = true;
        setBoard(nb);
        setSelected(null);
        // capture chain check
        const more = getMoves(nb, r, c).filter(m => m.capture);
        if (move.capture && more.length > 0) {
          setSelected({ r, c });
        } else {
          setTurn(t => t === "R" ? "B" : "R");
        }
        return;
      }
    }
    if (piece && piece.color === turn) setSelected({ r, c });
    else setSelected(null);
  };

  const reset = () => { setBoard(initialBoard()); setTurn("R"); setSelected(null); };

  return (
    <div data-testid="game-checkers" className="bg-black/60 border border-white/10 rounded-md p-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="sc-heading text-xl font-bold">Checkers</div>
        <div className="text-xs sc-label">
          {winner ? <span className="text-[#10B981]">Winner: {winner === "R" ? "Red" : "Black"}</span> :
            <>Turn: <span style={{ color: turn === "R" ? "#FF3366" : "#00E5FF" }}>{turn === "R" ? "Red" : "Black"}</span></>}
        </div>
      </div>
      <div className="inline-block border border-white/20 mx-auto block w-fit">
        {board.map((row, r) => (
          <div key={r} className="flex">
            {row.map((cell, c) => {
              const isDark = (r + c) % 2 === 1;
              const isSelected = selected && selected.r === r && selected.c === c;
              const isMove = moves.some(m => m.r === r && m.c === c);
              return (
                <button
                  data-testid={`checkers-${r}-${c}`}
                  key={c}
                  onClick={() => handleCellClick(r, c)}
                  className="w-12 h-12 flex items-center justify-center transition-colors"
                  style={{
                    background: isMove ? "rgba(16,185,129,0.25)" : isSelected ? "rgba(255,51,102,0.3)" : isDark ? "#27272a" : "#0c0c0e",
                    border: isSelected ? "1px solid #FF3366" : "1px solid rgba(255,255,255,0.04)",
                  }}
                >
                  {cell && (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-black"
                      style={{
                        background: cell.color === "R" ? "#FF3366" : "#18181B",
                        border: cell.color === "R" ? "2px solid #ff6688" : "2px solid #00E5FF",
                        color: cell.king ? "#F59E0B" : "transparent",
                      }}
                    >{cell.king ? "K" : ""}</div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-4 text-center text-xs text-zinc-400">Red: {R} pieces · Black: {B} pieces</div>
      <div className="mt-4 text-center">
        <button onClick={reset} className="sc-btn-ghost text-xs">Reset</button>
      </div>
    </div>
  );
}
